import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/atoms/modal'
import { Button, ButtonType } from '@/components/atoms/button'
import { useUnsavedChangesRouteGuard } from '@/hooks/use-unsaved-changes-route-guard'

type UnsavedLabel = {
  key: string
  label: string
}

type Props = {
  hasUnsaved: boolean
  unsavedLabels: UnsavedLabel[]
}

/**
 * React Router のルート遷移前(SPA内遷移前)に確認ダイアログを出す
 */
export const UnsavedChangesRouteGuard: React.FC<Props> = ({ hasUnsaved, unsavedLabels }) => {
  const { t } = useTranslation()
  const { pendingNavigation, confirmNavigation, cancelNavigation } = useUnsavedChangesRouteGuard(hasUnsaved)

  if (!pendingNavigation) return null

  return (
    <Modal customCss={dialogModalStyle}>
      <ModalHeader>{t('There are unsaved changes')}</ModalHeader>
      <ModalBody>
        <p css={descriptionStyle}>{t('The following sections have unsaved content. If you leave, your edits will be lost.')}</p>
        <ul css={sectionListStyle}>
          {unsavedLabels.map(({ key, label }) => (
            <li key={key}>{label}</li>
          ))}
        </ul>
      </ModalBody>
      <ModalFooter customCss={footerStyle}>
        <Button buttonType={ButtonType.Secondary} width={120} height={38} fontSize={13} marginRight={16} onClick={confirmNavigation}>
          {t('Discard and leave')}
        </Button>
        <Button buttonType={ButtonType.Primary} width={160} height={38} fontSize={13} fontWeight="bold" onClick={cancelNavigation}>
          {t('Stay on this page')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const dialogModalStyle = {
  height: 'auto',
  minHeight: 'unset',
}

const descriptionStyle = css({
  fontSize: 14,
  lineHeight: 1.6,
  color: '#272727',
  marginBottom: 12,
})

const sectionListStyle = css({
  paddingLeft: 20,
  li: {
    fontSize: 14,
    lineHeight: 2,
    color: '#272727',
    listStyleType: 'disc',
  },
})

const footerStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '16px 24px',
}
